import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { omit } from "lodash-es";

import {
  clearDataByKey,
  dataMetaSelector,
  getEventGoogleLocations,
  getEventSuggestedLocations,
  googleLocationsSelector,
  suggestedLocationsSelector,
} from "Bands/state";
import { BAND_ACTIONS, IEventSuggestedLocation } from "Bands/types";
import { IAPIState, IDataKeys } from "shared/types";
import {
  getSuggestedLocationsOptions,
  useBandRegionalSettings,
} from "Bands/utils";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { useEventContext } from "Bands/contexts";

export const useTravelLegLocationOptions = (
  defaultSuggestedLocations?: IEventSuggestedLocation[]
) => {
  const dispatch = useDispatch();
  const { dateFormat } = useBandRegionalSettings();
  const { eventId } = useEventContext();

  const suggestedLocations = useSelector(suggestedLocationsSelector);
  const googleLocations = useSelector(googleLocationsSelector);

  const [selectedDeparture, setSelectedDeparture] =
    useState<ISelectOption | null>(null);
  const [selectedArrival, setSelectedArrival] =
    useState<ISelectOption | null>(null);

  const { api: suggestedMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventSuggestedLocations)
  );
  const { api: googleMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventGoogleLocations)
  );

  // fetch suggested locations on mount
  useEffect(() => {
    if (!eventId) return;

    dispatch(getEventSuggestedLocations({ event: eventId }));
  }, [dispatch, eventId]);

  const fetchGoogleLocations = useCallback(
    (inputValue: string) => {
      dispatch(getEventGoogleLocations({ term: inputValue }));
    },
    [dispatch]
  );

  const resetLocations = () => {
    dispatch(clearDataByKey(IDataKeys.eventGoogleLocation));
  };

  const suggestedLocationOptions = useMemo(
    () =>
      getSuggestedLocationsOptions({
        locations: suggestedLocations || defaultSuggestedLocations,
        dateFormat,
      }),
    [dateFormat, suggestedLocations, defaultSuggestedLocations]
  );

  const googleLocationOptions = useMemo(
    () =>
      getSuggestedLocationsOptions({
        title: "Google locations",
        locations: googleLocations,
        dateFormat,
        alwaysShow: true,
      }),
    [dateFormat, googleLocations]
  );

  const options = useMemo((): ISelectOption[] => {
    let locations = [];

    const departureIsInSuggested = suggestedLocationOptions.find(
      ({ value }) => value === selectedDeparture?.value
    );

    const arrivalIsInSuggested = suggestedLocationOptions.find(
      ({ value }) => value === selectedArrival?.value
    );

    if (selectedDeparture && !departureIsInSuggested) {
      locations.push(omit(selectedDeparture, "alwaysShow"));
    }

    if (
      selectedArrival &&
      !arrivalIsInSuggested &&
      selectedArrival.value !== selectedDeparture?.value
    ) {
      locations.push(omit(selectedArrival, "alwaysShow"));
    }

    locations = locations.concat(
      suggestedLocationOptions,
      googleLocationOptions
    );

    return locations;
  }, [
    googleLocationOptions,
    selectedArrival,
    selectedDeparture,
    suggestedLocationOptions,
  ]);

  return {
    options,
    resetLocations,
    setSelectedArrival,
    setSelectedDeparture,
    fetchGoogleLocations,
    isFetchingLocations:
      suggestedMeta?.status === IAPIState.pending ||
      googleMeta?.status === IAPIState.pending,
  };
};
